import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { FileUploader } from '@/shared/components/custom/file-uploader/FileUploader';
import { useBulkImportHistoricalTimeOffRequests } from '../../hooks/useBulkImportHistoricalTimeOffRequests';
import { TIME_OFF_HISTORICAL_REQUESTS_ERROR_MAP } from '../../errors';
import type { BulkImportHistoricalResultDto } from '../../types';

const ACCEPTED_TYPES = {
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;

function rowErrorMessage(t: (key: string) => string, errorCode: string | null): string {
  const map = TIME_OFF_HISTORICAL_REQUESTS_ERROR_MAP.POST!;
  const key = (errorCode ? map[errorCode] : undefined) ?? map.default;
  return t(key);
}

interface HistoricalImportSectionProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

/**
 * Bulk historical-import via CSV/Excel — Owner/Admin only. The backend
 * parses the file: one row is one day, rows sharing
 * `user_email+type_code+cycle_number+reason` group into one request. Always
 * resolves with `{created, errors}` — a failing row never rejects the call.
 */
export function HistoricalImportSection({ onSuccess, onCancel }: HistoricalImportSectionProps) {
  const { t } = useTranslation('timeOff');
  const { mutate: importFile, isPending } = useBulkImportHistoricalTimeOffRequests();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<BulkImportHistoricalResultDto | null>(null);
  const [importError, setImportError] = useState(false);

  function handleFileAccepted(files: File[]) {
    setSelectedFile(files[0] ?? null);
    setResult(null);
    setImportError(false);
  }

  function handleSubmit() {
    if (!selectedFile) return;
    importFile(selectedFile, {
      onSuccess: (data) => {
        setResult(data);
        if (!data.errors?.length) onSuccess?.();
      },
      onError: () => setImportError(true),
    });
  }

  const createdCount = result?.created?.length ?? 0;
  const errors = result?.errors ?? [];

  return (
    <div className="space-y-4" data-testid="time-off-historical-import-section">
      <FileUploader
        accept={ACCEPTED_TYPES}
        maxSize={MAX_FILE_SIZE}
        onFilesAccepted={handleFileAccepted}
        disabled={isPending}
      >
        {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
          <div
            {...getRootProps()}
            data-testid="time-off-historical-import-file-uploader"
            className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              isDragReject
                ? 'border-destructive bg-destructive/5'
                : isDragActive
                  ? 'border-primary bg-primary/5'
                  : selectedFile
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-primary/40'
            }`}
          >
            <input data-testid="time-off-historical-import-file-input" {...getInputProps()} />
            {selectedFile ? (
              <>
                <FileText className="h-8 w-8 text-primary" />
                <p className="text-sm font-medium">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(selectedFile.size / 1024).toFixed(0)} KB
                </p>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium">{t('team.balances.importDialog.fileLabel')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('team.balances.importDialog.fileHint')}
                </p>
              </>
            )}
          </div>
        )}
      </FileUploader>

      {importError && (
        <div
          className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
          data-testid="time-off-historical-import-error"
        >
          <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
          <p className="text-sm text-destructive">{t('team.balances.importDialog.errorToast')}</p>
        </div>
      )}

      {result && (
        <div className="space-y-2" data-testid="time-off-historical-import-results">
          {createdCount > 0 && (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
              <p className="text-sm">
                {t('team.balances.importDialog.successToast', { count: createdCount })}
              </p>
            </div>
          )}

          {errors.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
                <p className="text-sm text-destructive">
                  {t('team.balances.importDialog.partialSuccessToast', {
                    created: createdCount,
                    errors: errors.length,
                  })}
                </p>
              </div>
              <ul className="max-h-32 overflow-y-auto rounded border p-2 text-xs text-muted-foreground">
                {errors.map((err) => (
                  <li key={err.rowIndex} className="py-0.5">
                    <span className="font-medium">
                      {t('team.balances.importDialog.errorRowLabel', { row: err.rowIndex })}:
                    </span>{' '}
                    {rowErrorMessage(t, err.errorCode)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('team.balances.importDialog.cancel')}
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isPending || !selectedFile}
          data-testid="time-off-historical-import-submit-btn"
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('team.balances.importDialog.submit')}
        </Button>
      </div>
    </div>
  );
}
